import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  ArrowLeft, 
  Save,
  Wrench,
  AlertCircle,
  User,
  Laptop,
  DollarSign,
  CheckCircle2,
  XCircle,
  Building2,
  Package,
  ClipboardCheck,
  Edit2,
  History
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn, formatDate, formatDateTime, formatCurrency } from '@/lib/utils';
import api from '@/services/api';
import AssignLoanerModal from '../components/AssignLoanerModal';
import ReceiveRepairModal from '../components/ReceiveRepairModal';

// Repair status color mapping
function getRepairStatusColor(status: string): string {
  const colors: Record<string, string> = {
    PENDING: 'bg-yellow-100 text-yellow-800',
    IN_TRANSIT: 'bg-blue-100 text-blue-800',
    AT_PROVIDER: 'bg-purple-100 text-purple-800',
    IN_REPAIR: 'bg-orange-100 text-orange-800',
    COMPLETED: 'bg-green-100 text-green-800',
    RETURNED: 'bg-green-100 text-green-800',
    WRITE_OFF: 'bg-red-100 text-red-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
}

function getRepairPriorityColor(priority: string): string {
  const colors: Record<string, string> = {
    LOW: 'bg-gray-100 text-gray-800',
    MEDIUM: 'bg-blue-100 text-blue-800',
    HIGH: 'bg-orange-100 text-orange-800',
    CRITICAL: 'bg-red-200 text-red-900',
  };
  return colors[priority] || 'bg-gray-100 text-gray-800';
}

function getRepairTypeColor(type: string): string {
  const colors: Record<string, string> = {
    CORRECTIVE: 'bg-red-50 text-red-700',
    PREVENTIVE: 'bg-blue-50 text-blue-700',
    WARRANTY: 'bg-green-50 text-green-700',
  };
  return colors[type] || 'bg-gray-100 text-gray-800';
}

const REPAIR_STATUSES = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'IN_TRANSIT', label: 'In Transit' },
  { value: 'AT_PROVIDER', label: 'At Provider' },
  { value: 'IN_REPAIR', label: 'In Repair' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'RETURNED', label: 'Returned' },
  { value: 'WRITE_OFF', label: 'Write-off' },
];

const REPAIR_TYPES = [
  { value: 'CORRECTIVE', label: 'Corrective' },
  { value: 'PREVENTIVE', label: 'Preventive' },
  { value: 'WARRANTY', label: 'Warranty' },
];

const PRIORITIES = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
  { value: 'CRITICAL', label: 'Critical' },
];

export default function RepairDetailsPage() {
  const { id } = useParams();
  const queryClient = useQueryClient();
  
  const [isEditing, setIsEditing] = useState(false);
  const [showLoanerModal, setShowLoanerModal] = useState(false);
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    status: '',
    priority: '',
    repairType: '',
    issueDescription: '',
    diagnosisNotes: '',
    repairNotes: '',
    estimatedCost: '',
    actualCost: '',
    estimatedDays: '',
    partsReplaced: '',
    qualityCheckPassed: false,
    qualityCheckNotes: '',
  });

  // Fetch repair details
  const { data: repair, isLoading, error } = useQuery({
    queryKey: ['repair', id],
    queryFn: async () => {
      const response = await api.get(`/repairs/${id}`);
      return response.data.data;
    },
    enabled: Boolean(id),
  });

  // Update form when repair data loads
  useState(() => {
    if (repair) {
      setFormData({
        status: repair.status || '',
        priority: repair.priority || '',
        repairType: repair.repairType || '',
        issueDescription: repair.issueDescription || '',
        diagnosisNotes: repair.diagnosisNotes || '',
        repairNotes: repair.repairNotes || '',
        estimatedCost: repair.estimatedCost?.toString() || '',
        actualCost: repair.actualCost?.toString() || '',
        estimatedDays: repair.estimatedDays?.toString() || '',
        partsReplaced: repair.partsReplaced || '',
        qualityCheckPassed: repair.qualityCheckPassed || false,
        qualityCheckNotes: repair.qualityCheckNotes || '',
      });
    }
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const payload = {
        ...data,
        estimatedCost: data.estimatedCost ? parseFloat(data.estimatedCost) : null,
        actualCost: data.actualCost ? parseFloat(data.actualCost) : null,
        estimatedDays: data.estimatedDays ? parseInt(data.estimatedDays) : null,
      };
      const response = await api.put(`/repairs/${id}`, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['repair', id] });
      queryClient.invalidateQueries({ queryKey: ['repairs'] });
      queryClient.invalidateQueries({ queryKey: ['sanalam-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['asset-statistics'] });
      setIsEditing(false);
      setSubmitError(null);
    },
    onError: (error: any) => {
      setSubmitError(error.response?.data?.message || 'Failed to update repair');
    },
  });

  // Return loaner mutation
  const returnLoanerMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/repairs/${id}/return-loaner`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['repair', id] });
      queryClient.invalidateQueries({ queryKey: ['repairs'] });
    },
    onError: (error: any) => {
      setSubmitError(error.response?.data?.message || 'Failed to return loaner');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };

  const handleStartEdit = () => {
    if (repair) {
      setFormData({
        status: repair.status || '',
        priority: repair.priority || '',
        repairType: repair.repairType || '',
        issueDescription: repair.issueDescription || '',
        diagnosisNotes: repair.diagnosisNotes || '',
        repairNotes: repair.repairNotes || '',
        estimatedCost: repair.estimatedCost?.toString() || '',
        actualCost: repair.actualCost?.toString() || '',
        estimatedDays: repair.estimatedDays?.toString() || '',
        partsReplaced: repair.partsReplaced || '',
        qualityCheckPassed: repair.qualityCheckPassed || false,
        qualityCheckNotes: repair.qualityCheckNotes || '',
      });
    }
    setIsEditing(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !repair) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 mx-auto text-destructive mb-4" />
        <p className="text-lg font-medium">Repair not found</p>
        <p className="text-muted-foreground mb-4">The repair record you're looking for doesn't exist.</p>
        <Button asChild>
          <Link to="/repairs">Back to Repairs</Link>
        </Button>
      </div>
    );
  }

  const canAssignLoaner = !repair.loanerAssetId && repair.status !== 'RETURNED' && repair.status !== 'WRITE_OFF';
  const canReturnLoaner = repair.loanerAssetId && !repair.loanerReturnedDate;
  const canReceiveRepair = repair.status === 'COMPLETED';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/repairs">
              <ArrowLeft className="w-5 h-5" />
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold">{repair.repairNumber}</h1>
              <span className={cn(
                "px-2 py-1 text-xs font-medium rounded-full",
                getRepairStatusColor(repair.status)
              )}>
                {repair.status.replace(/_/g, ' ')}
              </span>
            </div>
            <p className="text-muted-foreground">
              Created {formatDateTime(repair.createdAt)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isEditing && (
            <>
              {canAssignLoaner && (
                <Button variant="outline" onClick={() => setShowLoanerModal(true)}>
                  <Laptop className="w-4 h-4 mr-2" />
                  Assign Loaner
                </Button>
              )}
              {canReturnLoaner && (
                <Button 
                  variant="outline" 
                  onClick={() => returnLoanerMutation.mutate()}
                  disabled={returnLoanerMutation.isPending}
                >
                  <Package className="w-4 h-4 mr-2" />
                  Return Loaner
                </Button>
              )}
              {canReceiveRepair && (
                <Button variant="outline" onClick={() => setShowReceiveModal(true)}>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Receive from Repair
                </Button>
              )}
              <Button onClick={handleStartEdit}>
                <Edit2 className="w-4 h-4 mr-2" />
                Edit
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Error Alert */}
      {submitError && (
        <Card className="border-destructive bg-destructive/10">
          <CardContent className="flex items-center gap-2 p-4">
            <AlertCircle className="w-5 h-5 text-destructive" />
            <p className="text-destructive">{submitError}</p>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Repair Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wrench className="w-5 h-5" />
                Repair Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isEditing ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-3">
                    <div className="space-y-2">
                      <Label htmlFor="status">Status</Label>
                      <select
                        id="status"
                        className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      >
                        {REPAIR_STATUSES.map(s => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="priority">Priority</Label>
                      <select
                        id="priority"
                        className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                        value={formData.priority}
                        onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                      >
                        {PRIORITIES.map(p => (
                          <option key={p.value} value={p.value}>{p.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="repairType">Repair Type</Label>
                      <select
                        id="repairType"
                        className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                        value={formData.repairType}
                        onChange={(e) => setFormData({ ...formData, repairType: e.target.value })}
                      >
                        {REPAIR_TYPES.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="issueDescription">Issue Description</Label>
                    <textarea
                      id="issueDescription"
                      className="w-full min-h-[100px] p-3 rounded-md border border-input bg-background text-sm resize-none"
                      value={formData.issueDescription}
                      onChange={(e) => setFormData({ ...formData, issueDescription: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="diagnosisNotes">Diagnosis Notes</Label>
                    <textarea
                      id="diagnosisNotes"
                      className="w-full min-h-[80px] p-3 rounded-md border border-input bg-background text-sm resize-none"
                      value={formData.diagnosisNotes}
                      onChange={(e) => setFormData({ ...formData, diagnosisNotes: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="repairNotes">Repair Notes</Label>
                    <textarea
                      id="repairNotes"
                      className="w-full min-h-[80px] p-3 rounded-md border border-input bg-background text-sm resize-none"
                      value={formData.repairNotes}
                      onChange={(e) => setFormData({ ...formData, repairNotes: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="partsReplaced">Parts Replaced</Label>
                    <Input
                      id="partsReplaced"
                      placeholder="List parts that were replaced"
                      value={formData.partsReplaced}
                      onChange={(e) => setFormData({ ...formData, partsReplaced: e.target.value })}
                    />
                  </div>

                  <div className="grid gap-4 sm:grid-cols-3">
                    <div className="space-y-2">
                      <Label htmlFor="estimatedCost">Estimated Cost</Label>
                      <Input
                        id="estimatedCost"
                        type="number"
                        step="0.01"
                        value={formData.estimatedCost}
                        onChange={(e) => setFormData({ ...formData, estimatedCost: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="actualCost">Actual Cost</Label>
                      <Input
                        id="actualCost"
                        type="number"
                        step="0.01"
                        value={formData.actualCost}
                        onChange={(e) => setFormData({ ...formData, actualCost: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="estimatedDays">Estimated Days</Label>
                      <Input
                        id="estimatedDays"
                        type="number"
                        value={formData.estimatedDays}
                        onChange={(e) => setFormData({ ...formData, estimatedDays: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-medium mb-3">Quality Check</h4>
                    <div className="flex items-center gap-2 mb-3">
                      <input
                        type="checkbox"
                        id="qualityCheckPassed"
                        checked={formData.qualityCheckPassed}
                        onChange={(e) => setFormData({ ...formData, qualityCheckPassed: e.target.checked })}
                        className="w-4 h-4 rounded border-input"
                      />
                      <Label htmlFor="qualityCheckPassed">Quality Check Passed</Label>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="qualityCheckNotes">Quality Check Notes</Label>
                      <textarea
                        id="qualityCheckNotes"
                        className="w-full min-h-[60px] p-3 rounded-md border border-input bg-background text-sm resize-none"
                        value={formData.qualityCheckNotes}
                        onChange={(e) => setFormData({ ...formData, qualityCheckNotes: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setIsEditing(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={updateMutation.isPending}>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="space-y-6">
                  <div className="grid gap-4 sm:grid-cols-3">
                    <div>
                      <p className="text-sm text-muted-foreground">Status</p>
                      <span className={cn(
                        "inline-block px-2 py-1 text-xs font-medium rounded-full mt-1",
                        getRepairStatusColor(repair.status)
                      )}>
                        {repair.status.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Priority</p>
                      <span className={cn(
                        "inline-block px-2 py-1 text-xs font-medium rounded-full mt-1",
                        getRepairPriorityColor(repair.priority)
                      )}>
                        {repair.priority}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Repair Type</p>
                      <span className={cn(
                        "inline-block px-2 py-1 text-xs font-medium rounded-full mt-1",
                        getRepairTypeColor(repair.repairType)
                      )}>
                        {repair.repairType}
                      </span>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Issue Description</p>
                    <p className="text-sm whitespace-pre-wrap">{repair.issueDescription || '-'}</p>
                  </div>

                  {repair.diagnosisNotes && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Diagnosis Notes</p>
                      <p className="text-sm whitespace-pre-wrap">{repair.diagnosisNotes}</p>
                    </div>
                  )}

                  {repair.repairNotes && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Repair Notes</p>
                      <p className="text-sm whitespace-pre-wrap">{repair.repairNotes}</p>
                    </div>
                  )}

                  {repair.partsReplaced && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Parts Replaced</p>
                      <p className="text-sm">{repair.partsReplaced}</p>
                    </div>
                  )}

                  {repair.qualityCheckPassed !== null && (
                    <div className="border-t pt-4">
                      <h4 className="font-medium mb-2">Quality Check</h4>
                      <div className="flex items-center gap-2">
                        {repair.qualityCheckPassed ? (
                          <CheckCircle2 className="w-5 h-5 text-green-500" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-500" />
                        )}
                        <span className="text-sm">
                          {repair.qualityCheckPassed ? 'Passed' : 'Failed'}
                        </span>
                      </div>
                      {repair.qualityCheckNotes && (
                        <p className="text-sm text-muted-foreground mt-2">{repair.qualityCheckNotes}</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Cost & Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Cost & Timeline
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6 sm:grid-cols-2">
                <div className="space-y-4">
                  <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Cost</h4>
                  <div className="grid gap-3">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Estimated Cost</span>
                      <span className="font-medium">
                        {repair.estimatedCost ? formatCurrency(repair.estimatedCost) : '-'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Actual Cost</span>
                      <span className="font-medium">
                        {repair.actualCost ? formatCurrency(repair.actualCost) : '-'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Currency</span>
                      <span className="font-medium">{repair.currency || 'USD'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Warranty Used</span>
                      <span className="font-medium">{repair.warrantyUsed ? 'Yes' : 'No'}</span>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Timeline</h4>
                  <div className="grid gap-3">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Estimated Days</span>
                      <span className="font-medium">{repair.estimatedDays || '-'} days</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Taken Date</span>
                      <span className="font-medium">
                        {repair.takenDate ? formatDate(repair.takenDate) : '-'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Expected Return</span>
                      <span className="font-medium">
                        {repair.expectedReturnDate ? formatDate(repair.expectedReturnDate) : '-'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Completed Date</span>
                      <span className="font-medium">
                        {repair.completedDate ? formatDate(repair.completedDate) : '-'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Received Date</span>
                      <span className="font-medium">
                        {repair.receivedDate ? formatDate(repair.receivedDate) : '-'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Loaner Information */}
          {(repair.loanerAssetId || repair.loanerAsset) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Laptop className="w-5 h-5" />
                  Loaner Asset
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <p className="text-sm text-muted-foreground">Loaner Asset</p>
                    <p className="font-medium">
                      {repair.loanerAsset?.assetTag || repair.loanerAssetId}
                      {repair.loanerAsset?.name && ` - ${repair.loanerAsset.name}`}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Issue Date</p>
                    <p className="font-medium">
                      {repair.loanerIssuedDate ? formatDate(repair.loanerIssuedDate) : '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Return Date</p>
                    <p className="font-medium">
                      {repair.loanerReturnedDate ? formatDate(repair.loanerReturnedDate) : 
                        <span className="text-orange-600">Not returned</span>}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Asset Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Laptop className="w-4 h-4" />
                Asset
              </CardTitle>
            </CardHeader>
            <CardContent>
              {repair.asset ? (
                <div className="space-y-3">
                  <div>
                    <Link 
                      to={`/assets/${repair.asset.id}`}
                      className="font-medium text-primary hover:underline"
                    >
                      {repair.asset.assetTag}
                    </Link>
                    <p className="text-sm text-muted-foreground">{repair.asset.name}</p>
                  </div>
                  {repair.asset.serialNumber && (
                    <div>
                      <p className="text-xs text-muted-foreground">Serial Number</p>
                      <p className="text-sm font-mono">{repair.asset.serialNumber}</p>
                    </div>
                  )}
                  {repair.asset.brand && (
                    <div>
                      <p className="text-xs text-muted-foreground">Brand / Model</p>
                      <p className="text-sm">{repair.asset.brand.name} {repair.asset.model}</p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">No asset linked</p>
              )}
            </CardContent>
          </Card>

          {/* Employee Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <User className="w-4 h-4" />
                Employee
              </CardTitle>
            </CardHeader>
            <CardContent>
              {repair.employee ? (
                <div className="space-y-3">
                  <div>
                    <Link 
                      to={`/repairs/employee/${repair.employee.id}/history`}
                      className="font-medium text-primary hover:underline"
                    >
                      {repair.employee.firstName} {repair.employee.lastName}
                    </Link>
                    <p className="text-sm text-muted-foreground">{repair.employee.employeeId}</p>
                  </div>
                  {repair.employee.email && (
                    <div>
                      <p className="text-xs text-muted-foreground">Email</p>
                      <p className="text-sm">{repair.employee.email}</p>
                    </div>
                  )}
                  {repair.employee.department && (
                    <div>
                      <p className="text-xs text-muted-foreground">Department</p>
                      <p className="text-sm">{repair.employee.department.name}</p>
                    </div>
                  )}
                  <Button variant="outline" size="sm" className="w-full" asChild>
                    <Link to={`/repairs/employee/${repair.employee.id}/history`}>
                      <History className="w-4 h-4 mr-2" />
                      View Repair History
                    </Link>
                  </Button>
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">No employee linked</p>
              )}
            </CardContent>
          </Card>

          {/* Service Provider */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Building2 className="w-4 h-4" />
                Service Provider
              </CardTitle>
            </CardHeader>
            <CardContent>
              {repair.serviceProvider ? (
                <div className="space-y-3">
                  <div>
                    <p className="font-medium">{repair.serviceProvider.name}</p>
                  </div>
                  {repair.serviceProvider.contactEmail && (
                    <div>
                      <p className="text-xs text-muted-foreground">Email</p>
                      <p className="text-sm">{repair.serviceProvider.contactEmail}</p>
                    </div>
                  )}
                  {repair.serviceProvider.contactPhone && (
                    <div>
                      <p className="text-xs text-muted-foreground">Phone</p>
                      <p className="text-sm">{repair.serviceProvider.contactPhone}</p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">No service provider assigned</p>
              )}
            </CardContent>
          </Card>

          {/* Taken/Received By */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <ClipboardCheck className="w-4 h-4" />
                Handling
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-xs text-muted-foreground">Taken By</p>
                {repair.takenByUser ? (
                  <p className="text-sm font-medium">
                    {repair.takenByUser.firstName} {repair.takenByUser.lastName}
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">-</p>
                )}
                {repair.takenDate && (
                  <p className="text-xs text-muted-foreground">{formatDateTime(repair.takenDate)}</p>
                )}
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Received By</p>
                {repair.receivedByUser ? (
                  <p className="text-sm font-medium">
                    {repair.receivedByUser.firstName} {repair.receivedByUser.lastName}
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">-</p>
                )}
                {repair.receivedDate && (
                  <p className="text-xs text-muted-foreground">{formatDateTime(repair.receivedDate)}</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Modals */}
      <AssignLoanerModal
        open={showLoanerModal}
        onClose={() => setShowLoanerModal(false)}
        repairId={id!}
        onSuccess={() => {
          setShowLoanerModal(false);
          queryClient.invalidateQueries({ queryKey: ['repair', id] });
        }}
      />

      <ReceiveRepairModal
        open={showReceiveModal}
        onClose={() => setShowReceiveModal(false)}
        repairId={id!}
        onSuccess={() => {
          setShowReceiveModal(false);
          queryClient.invalidateQueries({ queryKey: ['repair', id] });
        }}
      />
    </div>
  );
}
